import journeysQueries from "apps/journeys/utils/gql/queries";
import { fetchFromGraphql } from "entities/helpers/gqlCall";
import { formatSingleDate } from "apps/journeys/utils/helpers/dates";
import { fetchJourneyTitlesAndCounts } from "apps/journeys/utils/api-requests/journey-index";
import { reduceJourneysTaskDueCounts, mapTaskCounts } from "../helpers";

// For Today Card's "Other Task Counts"
// Returns an object with the count of tasks for each action type, plus "total" and "completed" counts
/* 
Example return value:
{
  total: 0,
  completed: 0,
  Email: 0,
  Call: 0,
  Other: 0,
  LinedIn: 0,
  Print Mail: 0,
  ThankView: 0,
  Text: 0,
}

*/
export const getTaskCount = async (
  selectedDate, // required
  taskOwnerUserId, // required
  taskStatuses, // required
  journeyTaskIds = null,
  taskActionTypes
) => {
  const query = journeysQueries.fetchTaskCount;

  const params = {
    journeyTaskIds,
    selectedDate,
    taskOwnerUserId,
    taskStatuses,
    taskActionTypes,
  };

  const { taskCount } = await fetchFromGraphql(query, params, "taskCount");

  Object.keys(taskCount).forEach(function (key) {
    if (taskCount[key] === null) {
      taskCount[key] = 0;
    }
  });

  return taskCount;
};

const getMappedTaskCount = async (selectedDate, taskOwnerUserId, taskStatuses, journeyTaskIds, taskActionTypes) => {
  const taskCount = await getTaskCount(selectedDate, taskOwnerUserId, taskStatuses, journeyTaskIds, taskActionTypes);
  return mapTaskCounts(taskCount);
};

const isSaturday = (dayDate) => {
  return dayDate.getDay() === 6;
};

const getJourneyCountsForDate = async (selectedJourneyGroupId, taskOwnerUserId, taskStatuses, selectedDate) => {
  const journeysForDate = await fetchJourneyTitlesAndCounts(
    selectedJourneyGroupId,
    taskOwnerUserId,
    taskStatuses,
    selectedDate
  );
  return reduceJourneysTaskDueCounts(journeysForDate);
};

const getCountCallback = async (type, selectedJourneyGroupId, taskOwnerUserId, taskStatuses, selectedDate) => {
  let count;
  if (type === "journey") {
    count = await getJourneyCountsForDate(selectedJourneyGroupId, taskOwnerUserId, taskStatuses, selectedDate);
  } else {
    count = await getMappedTaskCount(selectedDate, taskOwnerUserId, taskStatuses);
  }

  return { name: formatSingleDate(selectedDate), date: selectedDate, ...count };
};

const fetchNextFive = async (type, selectedJourneyGroupId, taskOwnerUserId, taskStatuses) => {
  const today = new Date();
  const todayCount = await getCountCallback(
    type,
    selectedJourneyGroupId,
    taskOwnerUserId,
    taskStatuses,
    today.getTime()
  );
  const counts = [todayCount];
  let date = new Date();
  date.setDate(today.getDate() + 1);
  let startedOnSaturday = isSaturday(today);
  while (counts.length < 5) {
    const taskCount = await getCountCallback(
      type,
      selectedJourneyGroupId,
      taskOwnerUserId,
      taskStatuses,
      date.getTime()
    );
    if (isSaturday(date) && !startedOnSaturday) {
      const sundayDate = new Date();
      sundayDate.setDate(date.getDate() + 1);
      const sundayCount = await getCountCallback(
        type,
        selectedJourneyGroupId,
        taskOwnerUserId,
        taskStatuses,
        sundayDate
      );
      if (sundayCount.total > 0 || taskCount.total > 0) {
        counts.push(taskCount);
        counts.push(sundayCount);
      }
      date.setDate(date.getDate() + 2);
    } else {
      counts.push(taskCount);
      date.setDate(date.getDate() + 1);
    }
  }
  return counts.sort((a, b) => a.date - b.date).slice(0, 5);
};

export const fetchNextFiveDays = async (selectedJourneyGroupId, taskOwnerUserId, taskStatuses) => {
  const nextFiveJourneyTasks = await fetchNextFive("journey", selectedJourneyGroupId, taskOwnerUserId, taskStatuses);
  const nextFiveOtherTasks = await fetchNextFive("task", null, taskOwnerUserId, taskStatuses);

  const merged = [];

  nextFiveJourneyTasks.forEach((journeyDay) => {
    const found = nextFiveOtherTasks.find((taskDay) => taskDay.name === journeyDay.name);
    if (found) {
      const { name, date, ...counts } = found;
      Object.entries(counts).forEach(([key, value]) => {
        journeyDay[key] += value;
      });
    }
    merged.push(journeyDay);
  });

  nextFiveOtherTasks.forEach((taskDay) => {
    const found = merged.find((day) => day.name === taskDay.name);
    if (!found) {
      merged.push(taskDay);
    }
  });

  return merged.sort((a, b) => a.date - b.date).slice(0, 5);
};
